<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        v-on:content-shown="initPanel()"
        ref="basePanel"
    >
        <template slot="content">
            <div :id="_uid" ref="custom-form" class="custom-form-panel-display">
                <rich-content-display :elements="panel.elements"></rich-content-display>
            </div>
        </template>
    </base-panel>
</template>

<script>
import BasePanel from "@/components/panels/BasePanel"
import RichContentDisplay from "@/components/RichContentDisplay"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "CustomFormPanel",
    components: {
        BasePanel,
        RichContentDisplay,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
        },
        showHeaders: {
            type: Boolean,
            required: false,
            default: true,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            customFormContainer: null,
            choiceResponses: [],
            isDisabled: false,
            isInitialized: false,
        }
    },
    computed: {
        isSubmitted() {
            return this.savedResponses && this.savedResponses.length > 0
        },
        correctChoicesCount() {
            return this.panel.choices.filter((c) => c.isCorrect).length
        },
        inputFieldsDisabled() {
            return this.locked || (this.isSubmitted && this.panel.disable_input_after_submit)
        },
        showFeedback() {
            return this.panel.show_correctness && (this.panel.show_feedback_before_continuing || this.locked)
        },
        allFieldsCompleted() {
            return this.choiceResponses.filter(c => c.response && c.response != "").length >= this.panel.choices.length
        },
    },
    watch: {
        choiceResponses: function (value) {
            this.$emit("update:choiceResponses", value)
        },
        panelId() {
            this.populateSavedResponses()
        },
        savedResponses() {
            this.populateSavedResponses()
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
            if (value && this.panel.disable_input_after_submit && !this.isDisabled) {
                this.setInputDisabledState(true)
                this.isDisabled = true
            }
        },
        correctChoicesCount(val) {
            this.$emit("update:correctChoicesCount", val)
        },
        allFieldsCompleted(val) {
            this.$emit("update:isComplete", val)
        },
    },
    methods: {
        // Initialize form input elements based on configuration, using mostly vanilla js methods. We can't
        // do this with Vue code because the html comes from the rich text editor (raw). So we can't hook into the
        // inputs/elements as we normally would.
        initFormInputs() {
            let vm = this
            this.panel.choices.forEach((choice) => {
                let formInputSelector = `[data-block-id="${choice.form_block_id}"]`
                let formInput = vm.customFormContainer.querySelector(formInputSelector)
                if (formInput) {
                    // Set attributes on the input based on the choice's configuration.
                    formInput.setAttribute("placeholder", choice.placeholder)
                    formInput.setAttribute("type", choice.accepted_value_type)
                    formInput.setAttribute("maxlength", 200)

                    // Extract the form input element and place it within a new div container. This
                    // allows us to place the feedback message element within the same div as the form
                    // input, so that they're grouped together (stacked vertically).
                    const formInputParentElement = formInput.parentNode
                    const newInputContainer = document.createElement('div')
                    newInputContainer.classList.add("d-inline-block", "mr-sm-1", "ml-sm-1", "mw-100")
                    newInputContainer.style.width = choice.marker_width + "px"
                    formInputParentElement.insertBefore(newInputContainer, formInput)
                    newInputContainer.appendChild(formInput)

                    // Replace basic input elements with the correct form element types depending
                    // on the accepted value type.
                    if (choice.accepted_value_type === "select") {
                        // Create an element for the select, to replace the existing input field.
                        let selectEl = document.createElement("select")
                        selectEl.setAttribute("data-block-id", choice.form_block_id)
                        selectEl.setAttribute("data-block-type", "input")
                        selectEl.setAttribute("class", "form-control form-control-sm")
                        selectEl.setAttribute("disabled", true)
                        // Add the placeholder as the initially selected option in the
                        // select dropdown.
                        let placeholderEl = document.createElement("option")
                        placeholderEl.setAttribute("value", "")
                        placeholderEl.setAttribute("disabled", true)
                        placeholderEl.setAttribute("selected", true)
                        placeholderEl.innerText = `${choice.placeholder}`
                        selectEl.appendChild(placeholderEl)
                        // Add each configured option to the select element.
                        choice.accepted_value_options.forEach(function (option) {
                            let optionEl = document.createElement("option")
                            optionEl.setAttribute("value", option)
                            optionEl.innerText = option
                            selectEl.appendChild(optionEl)
                        })
                        // Add the select element beside the form input.
                        formInput.insertAdjacentElement("afterend", selectEl)
                        // Remove the original input element.
                        formInput.remove()
                        // Replace the reference to the input with the new select element.
                        formInput = vm.customFormContainer.querySelector(formInputSelector)

                        if (!vm.savedResponses.length) {
                            // Select the first option by default.
                            vm.updateInputResponse(choice, formInput, "")
                        }
                    } else if (choice.accepted_value_type === "textarea") {
                        // Create an element for the select, to replace the existing input field.
                        let selectEl = document.createElement("textarea")
                        selectEl.setAttribute("data-block-id", choice.form_block_id)
                        selectEl.setAttribute("data-block-type", "input")
                        selectEl.setAttribute("class", "form-control form-control-sm")
                        selectEl.setAttribute("disabled", true)
                        selectEl.setAttribute("placeholder", choice.placeholder)
                        selectEl.setAttribute("rows", 3)
                        selectEl.setAttribute("maxlength", 3000)
                        // Add the select element beside the form input.
                        formInput.insertAdjacentElement("afterend", selectEl)
                        // Remove the original input element.
                        formInput.remove()
                        // Replace the reference to the input with the new select element.
                        formInput = vm.customFormContainer.querySelector(formInputSelector)
                    }

                    // Add the hint message as a popover, if configured.
                    if (choice.show_text_on_hover && !vm.reviewMode) {
                        let config = {
                            boundary: "window",
                            content: choice.hint_message,
                            trigger: "hover",
                            placement: "top",
                            customClass: "text-center",
                            template: `
                                <div class="popover shadow-sm hotspot-popover" role="tooltip">
                                    <div class="arrow"></div>
                                    <div class="p-2">
                                        <span class="icon icon-glyphicons-basic-lightbulb text-white"></span>
                                        <span class="popover-body ml-0 pl-0" style="margin-right: 2px;"></span>
                                    </div>
                                </div>
                            `,
                        }
                        $(`#${vm._uid} ${formInputSelector}`).popover(config)
                    }

                    if (!vm.inputFieldsDisabled) {
                        // Add a quick animation to each input field.
                        formInput.classList.add("form-control-animate")
                    }
                    // Set the width of the input.
                    formInput.style.width = choice.marker_width + "px"

                    // Add the feedback HTML element. Visiblity and contents will be toggled
                    // when feedback needs to be shown to the learner.
                    const feedbackHtml = `<span style="display: none;" class="invalid-feedback ml-1 mr-1 w-auto" data-feedback-id="${choice.form_block_id}"></span>`
                    const feedbackElement = document.createElement('div')
                    feedbackElement.innerHTML = feedbackHtml
                    newInputContainer.insertBefore(feedbackElement, newInputContainer.firstChild)

                    // Add the feedback icon element.
                    newInputContainer.insertAdjacentHTML(
                        "afterend",
                        `<span
                            style="display: none;"
                            data-feedback-icon-id="${choice.form_block_id}"
                            class="icon icon-glyphicons icon-glyphicons-basic-circle-empty-check mr-1 text-success">
                        </span>`
                    )

                    // Handle input to form inputs.
                    formInput.addEventListener("input", (event) => {
                        let formBlockId = event.target.getAttribute("data-block-id")
                        // Find the associated choice.
                        let choice = vm.panel.choices.find((c) => c.form_block_id == formBlockId)
                        if (choice) {
                            // Store the learner's input.
                            let learnerInput = event.target.value
                            vm.updateInputResponse(choice, formInput, learnerInput)
                        }
                    })
                }
            })
        },
        updateInputResponse(choice, formInputEl, learnerInput) {
            choice.learnerResponse = learnerInput
            // Check for an existing response in the stored list of responses.
            let existingResponse = this.choiceResponses.find((c) => c.id == choice.id)
            if (existingResponse) {
                // Update the existing response.
                existingResponse.response = learnerInput
            } else {
                // Append a new response to the list of responses.
                this.choiceResponses.push({
                    id: choice.id,
                    response: learnerInput,
                })
            }
            // If a correct value exists on the choice object (this happens after the learner
            // submits a response), re-check to see if their response is correct or incorrect.
            if (typeof choice.correct_values !== "undefined" && this.isSubmitted) {
                let learnerResponseCleaned = choice.learnerResponse.trim().toLowerCase()
                let correctValuesCleaned = choice.correct_values.map(c => c.trim().toLowerCase())
                let exactMatch = correctValuesCleaned.includes(learnerResponseCleaned)
                let anythingMatch = Boolean(choice.learnerResponse) && choice.correct_values.includes("*")
                let isCorrect = exactMatch || anythingMatch
                this.setInputFeedback(isCorrect, choice, formInputEl)
            }
        },
        // Populate saved responses to the custom form.
        populateSavedResponses() {
            let vm = this
            this.savedResponses.forEach((response) => {
                let formInput = vm.customFormContainer.querySelector(
                    `[data-block-id="${response.choice.form_block_id}"]`
                )
                if (!formInput) {
                    // Form input may have been removed from the form after the
                    // learner completed it.
                    return
                }

                if (!vm.panel.clear_inputs_when_returning || vm.reviewMode) {
                    // Set the value of the form input to the learner's provided value.
                    formInput.value = response.provided_value
                }

                // Store the correct_values and feedback message directly on the choice itself so
                // that we can easily check correctness as the learner updates their answer later on.
                let choice = this.panel.choices.find((c) => c.id == response.choice.id)
                if (choice) {
                    choice.correct_values = response.correct_values
                    choice.message = response.choice.message
                }
                // Set the correct/incorrect state on each input.
                vm.setInputFeedback(response.choice_correct, choice, formInput)
                // Add the saved responses to "live" responses object for editing/tracking.
                vm.choiceResponses.push({
                    id: choice.id,
                    response: response.provided_value,
                })
            })
            this.$emit("update:isSubmitted", this.isSubmitted)
        },
        // Set the correctness state of a form input.
        setInputFeedback(isCorrect, choice, formInput = null) {
            if (!formInput) {
                formInput = this.customFormContainer.querySelector(`[data-block-id="${choice.form_block_id}"]`)
            }

            // Fetch the feedback html elements associated with the input.
            let feedbackEl = this.customFormContainer.querySelector(`[data-feedback-id="${choice.form_block_id}"]`)
            let feedbackIconEl = this.customFormContainer.querySelector(
                `[data-feedback-icon-id="${choice.form_block_id}"]`
            )

            if (!feedbackEl || !feedbackIconEl) {
                return
            }

            if (choice.accepted_value_type == "textarea") {
                // Show feedback message if one is configured on the choice. This functionality deviates
                // from the convention of only showing feedback for incorrect responses, but it allows creators
                // to add reflection-style feedback on textarea inputs.
                feedbackEl.innerText = choice.message
                if (choice.message) {
                    feedbackEl.style.display = "inline-block"
                }
                // Don't set correctness for textarea inputs.
                return
            } else if (isCorrect && this.showFeedback) {
                formInput.classList.remove("is-invalid")
                formInput.classList.add("is-valid")
                feedbackEl.style.display = "none"
                // Don't show feedback icons if an asterisk is included as a correct answer.
                if (choice.correct_values.length && !choice.correct_values.includes("*")) {
                    feedbackIconEl.style.display = "inline"
                }
            } else if (this.showFeedback) {
                formInput.classList.remove("is-valid")
                formInput.classList.add("is-invalid")
                feedbackEl.innerText = choice.message
                feedbackIconEl.style.display = "none"
                if (choice.message) {
                    feedbackEl.style.display = "inline-block"
                }
            }
            // Store the correctness on the choice itself so we can easily count up the total
            // number of correct responses.
            this.$set(choice, "isCorrect", isCorrect)
        },
        // Set the disabled state of all form inputs.
        setInputDisabledState(disabledState) {
            let inputs = this.customFormContainer.querySelectorAll("[data-block-type='input']")
            inputs.forEach(function (el) {
                if (disabledState) {
                    el.setAttribute("disabled", true)
                } else {
                    el.removeAttribute("disabled")
                }
            })
        },
        initPanel() {
            if (this.isInitialized) {
                // Make sure the intialization happens only once.
                return
            }
            this.customFormContainer = this.$refs["custom-form"]
            this.initFormInputs()
            // Set the disabled state of all input fields.
            this.setInputDisabledState(this.inputFieldsDisabled)
            if (this.savedResponses.length) {
                this.populateSavedResponses()
            } else {
                this.$emit("update:choiceResponses", [])
            }
            this.isInitialized = true
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
        handleSubmit() {
            this.$emit("submit")
        },
    },
    beforeDestroy() {
        // Kill all popovers.
        Utilities.destroyAllPopovers()
    },
}
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

.custom-form-panel-display {
    .form-control {
        display: inline-block;
        width: auto;
        max-width: 100%;
        &.is-valid {
            border-color: $success;
            &:focus {
                box-shadow: 0 0 0 0.05rem $success;
            }
        }
        margin-bottom: 2px;
        margin-top: 2px;
    }

    .invalid-feedback {
        margin-top: 0 !important;
        white-space: normal;
    }

    .form-control-animate {
        animation: pulse-sm-primary 1.4s 1;
        animation-delay: 0.08s;
    }

    .invalid-feedback {
        font-size: 12.8px;
    }

    // Handles tables containing inputs overflowing on small screens.
    @media (max-width: $breakpoint-sm) {
        table {
            white-space: nowrap;
            overflow-x: scroll;
        }
        overflow-x: auto;
    }

    textarea.form-control {
        margin-top: 8px;
        margin-bottom: 8px;
    }
}
</style>